import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { I18n } from '@utils/i18n';
import { toast } from 'react-toastify';
import { SHIFT_STOPS_QUERY_KEY } from './useShiftStops';
import { isStopCauseLockedError } from '@clean-code/utils/stopAnnotation';

export interface StopClassificationResponse {
  machineId: number;
  startTime: number;
  endTime?: number;
  generalCause?: string | undefined;
  specificCause?: string | undefined;
  generalCauseId?: string | undefined;
  specificCauseId?: string | undefined;
  description?: string | undefined;
  isProgrammed?: boolean;
  id?: number | undefined;
  componentId?: string | undefined;
  componentName?: string | undefined;
  updatedAt?: Date;
  previousUpdatedAt?: Date;
}

export interface UpdateStopClassificationRequest {
  machineId: number;
  startTime: number;
  generalCauseId?: string | null;
  specificCauseId?: string | null;
  componentId?: string | null;
  description?: string | null;
  previousUpdatedAt?: string | null;
}

export const useUpdateStopClassificationBulk = () => {
  const queryClient = useQueryClient();
  return useMutation<
    StopClassificationResponse[],
    Error,
    UpdateStopClassificationRequest[]
  >({
    mutationFn: async (payload: UpdateStopClassificationRequest[]) => {
      const { data } = await api.put('/v2/stops/classifications/bulk', payload);
      return data;
    },
    onSuccess: () => {
      // Mark stale only: refetching here would reset rows in the open grid.
      queryClient.invalidateQueries({
        queryKey: [SHIFT_STOPS_QUERY_KEY],
        refetchType: 'none'
      });
      toast.dismiss();
      toast.success(I18n.t('stop_classification_updated_successfully'), {
        closeOnClick: true as any,
        autoClose: 1500,
        pauseOnFocusLoss: false
      });
    },
    onError: (error) => {
      toast.error(
        I18n.t(
          isStopCauseLockedError(error)
            ? 'stop_cause_locked_error'
            : 'error_updating_stop_classification'
        )
      );
    }
  });
};
